import { RefObject, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { PassLink } from '@bai/components';
import { useHasRole } from '@bai/features/user';
import { RoleNames } from '@bai/utils/types';

declare global {
  interface Window {
    getAngularContext?: () => {
      role: string;
      tab: string;
      manual: string;
    };
  }
}

type HelpTarget = {
  path: string;
  anchor: string;
  internalPath?: string;
} | null;

type UniversalHeaderHelpProps = {
  containerRef: RefObject<HTMLDivElement | null>;
};

function resolveHelpTarget({
  pathname,
  hash,
  isStudent,
  isInstructor,
  isManager,
}: {
  pathname: string;
  hash: string;
  isStudent: boolean;
  isInstructor: boolean;
  isManager: boolean;
}): HelpTarget {
  const currentPath = pathname.toLowerCase();

  if (currentPath.includes('/help')) {
    return null;
  }

  // For all student pages, navigate to the LMS Help page in the same tab
  if (
    currentPath.includes('/student/home') ||
    currentPath.includes('/student/transcripts') ||
    currentPath.endsWith('/catalog') ||
    currentPath.includes('/catalog/') ||
    currentPath.endsWith('/documents-catalog') ||
    currentPath.includes('/documents-catalog/') ||
    currentPath.endsWith('/student/ilt-calendar') ||
    currentPath.includes('/student/ilt-calendar/') ||
    currentPath.includes('/student/profile') ||
    currentPath.includes('/student/messages') ||
    currentPath.includes('/admin/messages') ||
    currentPath.endsWith('/proxy') ||
    currentPath.includes('/proxy/')
  ) {
    return {
      path: '',
      anchor: '',
      internalPath: '/help',
    };
  }

  let angularContext: { role: string; tab: string; manual: string } | null =
    null;

  if (window.getAngularContext) {
    try {
      angularContext = window.getAngularContext();
    } catch (error) {
      void error;
    }
  }

  if (angularContext) {
    if (angularContext.role === 'ta' || angularContext.role === 'admin') {
      return {
        path: '/learning-manager/technical-support/',
        anchor: hash || '',
      };
    }

    if (
      angularContext.role === 'manager' &&
      currentPath.includes('/users/home')
    ) {
      return {
        path: '/learning-manager/manager-user-manual/',
        anchor: hash || '',
      };
    }
    if (
      currentPath.includes('/users/home') &&
      angularContext?.role === 'instructor'
    ) {
      return {
        path: '/learning-manager/instructor-user-manual/',
        anchor: hash || '',
      };
    }

    if (
      currentPath.includes('/insights/home') &&
      (angularContext.role === 'decisionMaker' ||
        angularContext.tab === 'training insights')
    ) {
      return {
        path: '/learning-manager/training-insights-user-manual/',
        anchor: hash || '',
      };
    }
  }

  if (currentPath.includes('/reports') || currentPath.includes('report')) {
    return {
      path: '/learning-manager/reports-user-manual/',
      anchor: hash || '',
    };
  }

  return {
    path: '',
    anchor: '',
    internalPath: '/help',
  };
}

export default function UniversalHeaderHelp({
  containerRef,
}: UniversalHeaderHelpProps) {
  const navigate = useNavigate();

  const isStudent = useHasRole(RoleNames.Student);
  const isInstructor = useHasRole(RoleNames.Instructor);
  const isManager = useHasRole(RoleNames.Manager);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return undefined;
    }

    const clickHandler = (event: Event): void => {
      const target = event.target as HTMLElement | null;
      const helpIcon = target?.closest(
        '[data-cy="unified-header-help-icon"]',
      ) as HTMLAnchorElement | null;

      if (!helpIcon || !container.contains(helpIcon)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if ('stopImmediatePropagation' in event) {
        event.stopImmediatePropagation();
      }

      helpIcon.href = '#';

      const helpTarget = resolveHelpTarget({
        pathname: window.location.pathname,
        hash: window.location.hash,
        isStudent,
        isInstructor,
        isManager,
      });

      if (helpTarget?.internalPath) {
        navigate(helpTarget.internalPath);
        return;
      }

      if (!helpTarget?.path) {
        return;
      }

      void PassLink({
        path: helpTarget.path,
        anchor: helpTarget.anchor,
      });
    };

    container.addEventListener('click', clickHandler);

    return () => {
      container.removeEventListener('click', clickHandler);
    };
  }, [containerRef, navigate]);

  return null;
}
